import { useState, useEffect, useMemo } from 'react';
import db from '@/db';
import { getRecentSessions, summarize } from '@/lib/cashDrawer';
import { formatCurrency, formatDateTime } from '@/lib/formatters';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Vault, Lock, Unlock, ArrowDownToLine, ArrowUpFromLine, Banknote, CreditCard, QrCode,
  Clock, Download, Filter, AlertTriangle, CheckCircle2, FileText, Eye,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const PERIODS = [
  { id: 'today', label: 'Hoje' },
  { id: '7d', label: '7 dias' },
  { id: '30d', label: '30 dias' },
  { id: 'all', label: 'Tudo' },
];

function withinPeriod(iso, period) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  const now = Date.now();
  if (period === 'today') {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return t >= start.getTime();
  }
  if (period === '7d') return t >= now - 7 * 24 * 60 * 60 * 1000;
  if (period === '30d') return t >= now - 30 * 24 * 60 * 60 * 1000;
  return true;
}

function StatCard({ label, value, icon, tone = 'slate', testid }) {
  const toneCls = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  }[tone];
  return (
    <div data-testid={testid} className={`border rounded-xl px-4 py-3 ${toneCls}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-80">
        {icon} {label}
      </div>
      <p className="font-mono text-lg font-black mt-0.5">{value}</p>
    </div>
  );
}

function DiffBadge({ value }) {
  if (value === undefined || value === null) {
    return <Badge className="bg-slate-100 text-slate-500 border-slate-200">—</Badge>;
  }
  const v = Number(value);
  if (Math.abs(v) < 0.005) {
    return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle2 size={10} className="mr-1" />Bate</Badge>;
  }
  if (v > 0) {
    return <Badge className="bg-blue-100 text-blue-700 border-blue-200">+{formatCurrency(v)}</Badge>;
  }
  return <Badge className="bg-rose-100 text-rose-700 border-rose-200"><AlertTriangle size={10} className="mr-1" />{formatCurrency(v)}</Badge>;
}

export default function CashDrawerHistoryPage() {
  const [sessions, setSessions] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [operatorFilter, setOperatorFilter] = useState('all');

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [list, ops] = await Promise.all([
        getRecentSessions(200),
        db.operators.toArray(),
      ]);
      setSessions(list);
      setOperators(ops);
      setLoading(false);
    })();
  }, []);

  // Filtros (período em openedAt; status; operador; busca por id ou nome)
  const filtered = useMemo(() => {
    return sessions.filter((s) => {
      if (!withinPeriod(s.openedAt, period)) return false;
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (operatorFilter !== 'all' && Number(s.openedBy) !== Number(operatorFilter)) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${s.id} ${s.openedByName || ''} ${s.closedByName || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [sessions, period, statusFilter, operatorFilter, search]);

  // Agregados (apenas sessões fechadas pra evitar somas parciais)
  const aggregates = useMemo(() => {
    const closed = filtered.filter((s) => s.status === 'closed');
    let totalSales = 0;
    let cash = 0;
    let card = 0;
    let pix = 0;
    let other = 0;
    let sangrias = 0;
    let suprimentos = 0;
    let totalDiff = 0;
    let withDiffCount = 0;
    for (const s of closed) {
      const sum = summarize(s);
      totalSales += sum.totalSales;
      cash += sum.cash;
      card += sum.card;
      pix += sum.pix;
      other += sum.other;
      sangrias += sum.sangria;
      suprimentos += sum.suprimento;
      const d = Number(s.difference || 0);
      if (Math.abs(d) >= 0.005) { totalDiff += d; withDiffCount += 1; }
    }
    return {
      count: closed.length,
      totalSales,
      cash, card, pix, other,
      sangrias, suprimentos,
      totalDiff,
      withDiffCount,
    };
  }, [filtered]);

  const exportCsv = () => {
    if (filtered.length === 0) {
      toast.info('Nenhuma sessão no período');
      return;
    }
    const header = [
      'sessao_id', 'status', 'aberto_em', 'fechado_em', 'aberto_por', 'fechado_por',
      'fundo_inicial', 'vendas_dinheiro', 'vendas_cartao', 'vendas_pix', 'vendas_outros',
      'sangrias', 'suprimentos', 'esperado_dinheiro', 'contado_dinheiro', 'diferenca',
      'comandas_fechadas',
    ];
    const lines = [header.join(';')];
    for (const s of filtered) {
      const sum = summarize(s);
      const row = [
        s.id,
        s.status,
        s.openedAt || '',
        s.closedAt || '',
        s.openedByName || '',
        s.closedByName || '',
        sum.initialFloat.toFixed(2),
        sum.cash.toFixed(2),
        sum.card.toFixed(2),
        sum.pix.toFixed(2),
        sum.other.toFixed(2),
        sum.sangria.toFixed(2),
        sum.suprimento.toFixed(2),
        sum.expectedCash.toFixed(2),
        Number(s.countedCash || 0).toFixed(2),
        Number(s.difference || 0).toFixed(2),
        sum.commandsClosedCount,
      ].map((v) => String(v).replace(/;/g, ','));
      lines.push(row.join(';'));
    }
    const csv = lines.join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kaven-caixa-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${filtered.length} sessão(ões) exportadas`);
  };

  return (
    <div data-testid="cash-history-page" className="p-6 lg:p-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
            Histórico do Caixa
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Sessões de caixa abertas e fechadas com conciliação financeira por período
          </p>
        </div>
        <button
          data-testid="cash-history-export"
          onClick={exportCsv}
          className="flex items-center gap-2 px-4 h-10 rounded-lg bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-colors"
        >
          <Download size={14} /> Exportar CSV
        </button>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-6">
        <StatCard testid="stat-sessions" label="Sessões" value={aggregates.count} icon={<Vault size={11} />} />
        <StatCard testid="stat-total-sales" label="Vendas" value={formatCurrency(aggregates.totalSales)} tone="slate" icon={<FileText size={11} />} />
        <StatCard testid="stat-cash" label="Dinheiro" value={formatCurrency(aggregates.cash)} tone="emerald" icon={<Banknote size={11} />} />
        <StatCard testid="stat-card" label="Cartão" value={formatCurrency(aggregates.card)} tone="blue" icon={<CreditCard size={11} />} />
        <StatCard testid="stat-pix" label="Pix" value={formatCurrency(aggregates.pix)} tone="emerald" icon={<QrCode size={11} />} />
        <StatCard testid="stat-sangrias" label="Sangrias" value={formatCurrency(aggregates.sangrias)} tone="rose" icon={<ArrowUpFromLine size={11} />} />
        <StatCard testid="stat-suprimentos" label="Suprimentos" value={formatCurrency(aggregates.suprimentos)} tone="blue" icon={<ArrowDownToLine size={11} />} />
        <StatCard
          testid="stat-diff"
          label={`Diferenças (${aggregates.withDiffCount})`}
          value={formatCurrency(aggregates.totalDiff)}
          tone={Math.abs(aggregates.totalDiff) < 0.005 ? 'emerald' : aggregates.totalDiff > 0 ? 'blue' : 'rose'}
          icon={<AlertTriangle size={11} />}
        />
      </div>

      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
        <Filter size={16} className="text-slate-400 shrink-0" />
        <div className="flex flex-wrap gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              data-testid={`filter-period-${p.id}`}
              onClick={() => setPeriod(p.id)}
              className={`px-3 h-9 rounded-lg text-xs font-bold border-2 transition-colors ${
                period === p.id
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="hidden sm:block w-px h-6 bg-slate-200" />
        <div className="flex flex-wrap gap-1">
          {[
            { id: 'all', label: 'Todos' },
            { id: 'open', label: 'Abertas' },
            { id: 'closed', label: 'Fechadas' },
          ].map((f) => (
            <button
              key={f.id}
              data-testid={`filter-status-${f.id}`}
              onClick={() => setStatusFilter(f.id)}
              className={`px-3 h-9 rounded-lg text-xs font-bold border-2 transition-colors ${
                statusFilter === f.id
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <select
          data-testid="filter-operator"
          value={operatorFilter}
          onChange={(e) => setOperatorFilter(e.target.value)}
          className="h-10 px-3 border-2 border-slate-200 rounded-lg bg-white text-xs font-bold text-slate-700 hover:border-slate-400"
        >
          <option value="all">Todos operadores</option>
          {operators.map((op) => (
            <option key={op.id} value={op.id}>{op.name}</option>
          ))}
        </select>
        <Input
          data-testid="cash-history-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por ID ou operador..."
          className="h-10 max-w-xs border-2 border-slate-200 rounded-lg"
        />
      </div>

      {/* Lista */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="text-center py-16 text-slate-400 text-sm">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Vault size={56} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 font-semibold">Nenhuma sessão encontrada</p>
            <p className="text-xs text-slate-400 mt-1">Ajuste os filtros ou abra uma sessão no Caixa.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70 border-b border-slate-200">
                <tr>
                  <Th>Sessão</Th>
                  <Th>Status</Th>
                  <Th>Aberto em</Th>
                  <Th>Operador</Th>
                  <Th align="right">Fundo</Th>
                  <Th align="right">Vendas</Th>
                  <Th align="right">Diferença</Th>
                  <Th align="center">Ações</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const sum = summarize(s);
                  return (
                    <tr
                      key={s.id}
                      data-testid={`session-row-${s.id}`}
                      className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                    >
                      <Td>
                        <span className="font-mono font-bold text-slate-900">#{s.id}</span>
                      </Td>
                      <Td>
                        {s.status === 'open' ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                            <Unlock size={10} className="mr-1" /> Aberto
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700 border-slate-200">
                            <Lock size={10} className="mr-1" /> Fechado
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <div className="flex items-center gap-1.5 text-slate-700">
                          <Clock size={12} className="text-slate-400" />
                          <span className="text-xs">{formatDateTime(s.openedAt)}</span>
                        </div>
                      </Td>
                      <Td>
                        <span className="font-semibold">{s.openedByName || '—'}</span>
                      </Td>
                      <Td align="right" mono>{formatCurrency(sum.initialFloat)}</Td>
                      <Td align="right" mono bold>{formatCurrency(sum.totalSales)}</Td>
                      <Td align="right">
                        {s.status === 'closed' ? <DiffBadge value={s.difference} /> : <span className="text-slate-400 text-xs">—</span>}
                      </Td>
                      <Td align="center">
                        <button
                          data-testid={`session-view-${s.id}`}
                          onClick={() => setSelected(s)}
                          className="inline-flex items-center gap-1 px-3 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50"
                        >
                          <Eye size={12} /> Detalhes
                        </button>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SessionDetailDialog session={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function Th({ children, align = 'left' }) {
  const cls = `px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-slate-500 text-${align}`;
  return <th className={cls}>{children}</th>;
}

function Td({ children, align = 'left', mono, bold }) {
  const cls = `px-4 py-3 text-${align} ${mono ? 'font-mono' : ''} ${bold ? 'font-bold text-slate-900' : 'text-slate-700'}`;
  return <td className={cls}>{children}</td>;
}

function SessionDetailDialog({ session, onClose }) {
  if (!session) return null;
  const sum = summarize(session);
  const movements = Array.isArray(session.movements) ? session.movements : [];
  const sales = movements.filter((m) => String(m.type).startsWith('sale_'));
  const movs = movements.filter((m) => !String(m.type).startsWith('sale_'));

  return (
    <Dialog open={!!session} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="session-detail-dialog"
        className="max-w-2xl bg-white border-2 border-slate-300 rounded-2xl max-h-[88vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
            {session.status === 'open' ? <Unlock size={20} className="text-emerald-600" /> : <Lock size={20} />}
            Sessão #{session.id} — {session.status === 'open' ? 'Aberta' : 'Fechada'}
          </DialogTitle>
          <DialogDescription>
            Detalhes financeiros completos, movimentos e diferença na conciliação.
          </DialogDescription>
        </DialogHeader>

        {/* Cabeçalho com info da sessão */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Mini label="Aberto" value={formatDateTime(session.openedAt)} />
          <Mini label="Por" value={session.openedByName || '—'} />
          <Mini label="Fechado" value={session.closedAt ? formatDateTime(session.closedAt) : '—'} />
          <Mini label="Por" value={session.closedByName || '—'} />
        </div>

        {/* Resumo financeiro */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-1.5 text-sm">
          <Row label="Fundo inicial" value={formatCurrency(sum.initialFloat)} />
          <Row label="Vendas em dinheiro" value={formatCurrency(sum.cash)} tone="emerald" />
          <Row label="Vendas no cartão" value={formatCurrency(sum.card)} tone="blue" />
          <Row label="Vendas no pix" value={formatCurrency(sum.pix)} tone="emerald" />
          {sum.other > 0 && <Row label="Outros pagamentos" value={formatCurrency(sum.other)} />}
          <Row label="Suprimentos" value={formatCurrency(sum.suprimento)} tone="blue" />
          <Row label="Sangrias" value={`-${formatCurrency(sum.sangria)}`} tone="rose" />
          <div className="border-t border-slate-300 my-1.5" />
          <Row label="Esperado em dinheiro" value={formatCurrency(sum.expectedCash)} bold />
          {session.status === 'closed' && (
            <>
              <Row label="Contado" value={formatCurrency(session.countedCash || 0)} bold />
              <div className="pt-2 flex justify-between items-center">
                <span className="text-sm font-bold text-slate-700">Diferença</span>
                <DiffBadge value={session.difference} />
              </div>
              {session.closeNote && (
                <p className="text-[11px] text-slate-500 italic mt-1">Obs: {session.closeNote}</p>
              )}
            </>
          )}
          <div className="border-t border-slate-200 mt-1.5 pt-1.5 flex justify-between text-[11px] text-slate-500">
            <span>Total vendido</span>
            <strong className="font-mono text-slate-700">{formatCurrency(sum.totalSales)}</strong>
          </div>
          <div className="flex justify-between text-[11px] text-slate-500">
            <span>Comandas fechadas</span>
            <strong className="font-mono text-slate-700">{sum.commandsClosedCount}</strong>
          </div>
        </div>

        {/* Movimentos manuais (sangria/suprimento) */}
        {movs.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
              Movimentos manuais ({movs.length})
            </p>
            <div className="space-y-1.5">
              {movs.map((m, idx) => (
                <MovementRow key={`mov-${idx}`} mov={m} />
              ))}
            </div>
          </div>
        )}

        {/* Vendas detalhadas */}
        {sales.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
              Vendas registradas ({sales.length})
            </p>
            <div className="max-h-60 overflow-y-auto space-y-1">
              {sales.map((m, idx) => (
                <MovementRow key={`sale-${idx}`} mov={m} compact />
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
      <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400">{label}</p>
      <p className="text-xs font-semibold text-slate-700 truncate">{value}</p>
    </div>
  );
}

function Row({ label, value, tone, bold }) {
  const cls = tone === 'rose' ? 'text-rose-700' : tone === 'blue' ? 'text-blue-700' : tone === 'emerald' ? 'text-emerald-700' : 'text-slate-700';
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <strong className={`font-mono ${bold ? 'text-slate-900 font-bold' : ''} ${cls}`}>{value}</strong>
    </div>
  );
}

const MOV_LABEL = {
  sangria: { label: 'Sangria', icon: ArrowUpFromLine, tone: 'rose' },
  suprimento: { label: 'Suprimento', icon: ArrowDownToLine, tone: 'blue' },
  sale_cash: { label: 'Venda dinheiro', icon: Banknote, tone: 'emerald' },
  sale_card: { label: 'Venda cartão', icon: CreditCard, tone: 'blue' },
  sale_pix: { label: 'Venda pix', icon: QrCode, tone: 'emerald' },
  sale_other: { label: 'Venda outro', icon: FileText, tone: 'slate' },
};

function MovementRow({ mov, compact }) {
  const meta = MOV_LABEL[mov.type] || MOV_LABEL.sale_other;
  const Icon = meta.icon;
  const toneCls = {
    rose: 'text-rose-600 bg-rose-50',
    blue: 'text-blue-600 bg-blue-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    slate: 'text-slate-600 bg-slate-50',
  }[meta.tone];
  return (
    <div className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border border-slate-100 ${compact ? '' : 'bg-white'}`}>
      <div className={`shrink-0 w-7 h-7 rounded-lg flex items-center justify-center ${toneCls}`}>
        <Icon size={13} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-slate-800">
          {meta.label}
          {mov.note && <span className="text-slate-500 font-normal"> • {mov.note}</span>}
        </p>
        <p className="text-[10px] text-slate-400">
          {formatDateTime(mov.at)} {mov.byName ? `• ${mov.byName}` : ''}
        </p>
      </div>
      <span className="font-mono text-sm font-bold text-slate-900 shrink-0">
        {formatCurrency(mov.amount || 0)}
      </span>
    </div>
  );
}
